import React, { useCallback, useEffect, useState, memo } from 'react';
import PT from 'prop-types';
import { useMutation } from '@apollo/client';
import map from 'lodash/map';
import { getUserId } from 'utils';
import { useAuth, useMediaQueryMatches } from 'hooks';
import { Box, FormHelperText, styled } from 'components';
import { useForm } from 'components/form';
import { Button, Input, Spinner } from 'components/shared';
import { ConfirmationDialog } from 'components/dialogs';
import { DEACTIVATE_ACCOUNT, UPDATE_EMPLOYER_PASSWORD } from 'api';
import styles from 'styles/Profile/EmployerProfile';
import SectionLayout from '../SectionLayout';

const StyledRoot = styled('div')(styles);


const Account = (props) => {
  const { expanded, jumpLink, onExpand, sectionName } = props;
  const { logout } = useAuth();
  const { isDesktopApp: isDesktop } = useMediaQueryMatches();

  const [isSuccess, setIsSuccess] = useState(false);
  const [isDeactivationAlertOpen, setIsDeactivationAlertOpen] = useState(false);

  const [postNewPwd, { data = {}, error: mutationErrors = {}, loading: newPwdLoading = false }] =
    useMutation(UPDATE_EMPLOYER_PASSWORD);

  const signOut = () => {
    logout();
  };

  const [postDeactivateAccount, { loading: deactivateLoading }] =
    useMutation(DEACTIVATE_ACCOUNT, {
      onCompleted: () => {
        signOut();
      }
    });

  const { set, $, withValidation, reset } = useForm({
    initial: { oldPwd: '', newPwd: '', repeatPwd: '' },
    validations: {
      oldPwd: 'presence',
      newPwd: 'presence',
      repeatPwd: {
        rules: [
          'presence',
          (value, { attrs }) => {
            if (!value) return;
            // eslint-disable-next-line consistent-return
            if (attrs.newPwd && value !== attrs.newPwd) return "Passwords don't match";
          }
        ],
        deps: ['newPwd']
      }
    }
  });

  useEffect(() => {
    if (data?.postEmployerNewPassword) {
      setIsSuccess(true);
      reset();
    }
  }, [data]);

  const submitNewPwd = withValidation(async (attrs) => {
    const userId = getUserId();
    const { oldPwd, newPwd } = attrs;
    setIsSuccess(false);
    if (userId) {
      postNewPwd({
        variables: {
          userId: parseInt(userId),
          pass: newPwd,
          oldPass: oldPwd
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      });
    }
  });

  const handleChange = useCallback((event, { name }) => {
    set(name, event.target.value);
  }, []);

  const handleUserDeactivate = () => {
    const userId = getUserId();
    setIsDeactivationAlertOpen(false);

    if (userId) {
      postDeactivateAccount({
        variables: { userId: parseInt(userId) }
      });
    }
  };

  return (
    <SectionLayout
      expanded={expanded}
      jumpLink={jumpLink}
      onExpand={onExpand}
      sectionName={sectionName}
    >
      <StyledRoot className="section account__container">
        <div className="account__pwdForm">
          <h3 className={`value account__title`}>Change Password</h3>
          <div className="account__formItem">
            <Input
              {...$('oldPwd', handleChange)}
              id="old-password-input"
              required
              type="password"
              variant="textfield"
              label="Old Password"
              labelClassName="label"
              withHelperText
              analyticParams={{
                key: 'Old password focused (employer profile)',
                trigger: 'focus'
              }}
              testID="old-password-input"
            />
          </div>
          <div className="account__doubleCol">
            <div className="account__formItem">
              <Input
                {...$('newPwd', handleChange)}
                id="new-password-input"
                required
                type="password"
                variant="textfield"
                label="New Password"
                labelClassName="label"
                withHelperText
                analyticParams={{
                  key: 'New password focused (employer profile)',
                  trigger: 'focus'
                }}
                testID="new-password-input"
              />
            </div>
            <div className="account__formItem">
              <Input
                {...$('repeatPwd', handleChange)}
                id="repeat-password-input"
                required
                type="password"
                variant="textfield"
                label="Repeat New Password"
                labelClassName="label"
                withHelperText
                analyticParams={{
                  key: 'Repeat password focused (employer profile)',
                  trigger: 'focus'
                }}
                testID="confirm-password-input"
              />
            </div>
          </div>
          <div>
            <Button
              variant="filled-primary"
              className="mainAction"
              disabled={newPwdLoading}
              endIcon={newPwdLoading ? <Spinner size={24} /> : null}
              onClick={submitNewPwd}
              testID="change-password-submit"
            >
              {isDesktop ? 'Change Password' : 'Change'}
            </Button>
            {mutationErrors?.graphQLErrors &&
              map(mutationErrors.graphQLErrors, (o, i, arr) => (
                <FormHelperText
                  key={`mutationError__${i}`}
                  error
                  style={{ marginBottom: i === arr.length - 1 ? 20 : 0 }}
                >
                  {o.message}
                </FormHelperText>
              ))}
            {isSuccess && (
              <Box textAlign="center" color="success.main">
                Password changed successfully
              </Box>
            )}
          </div>
        </div>

        <div>
          <h3 className="value account__title">Deactivate Account</h3>
          <Button
            variant="filled-primary"
            className="mainAction"
            disabled={deactivateLoading}
            endIcon={deactivateLoading ? <Spinner size={24} /> : null}
            onClick={() => setIsDeactivationAlertOpen(true)}
            testID="deactivate-account-button"
          >
            Deactivate
          </Button>

          <ConfirmationDialog
            isOpen={isDeactivationAlertOpen}
            title="Warning"
            message="This action will deactivate your account. Do you want to continue?"
            confirmBtnName="Ok"
            onConfirm={handleUserDeactivate}
            onCancel={() => setIsDeactivationAlertOpen(false)}
            onClose={() => setIsDeactivationAlertOpen(false)}
          />
        </div>
      </StyledRoot>
    </SectionLayout>
  );
}

Account.propTypes = {
  expanded: PT.bool.isRequired,
  jumpLink: PT.string,
  onExpand: PT.func.isRequired,
  sectionName: PT.string.isRequired
};

Account.defaultProps = {
  jumpLink: ''
};

export default memo(Account);
